import React, { useEffect, useRef, useState } from 'react';
import { Form, Badge, Button, InputGroup, FormControl, ListGroup } from 'react-bootstrap';
import { getUserTagsClient } from '../../services/api-clients.service';
import { ActionMeta, components, CSSObjectWithLabel, GroupBase, MultiValue, MultiValueGenericProps, MultiValueProps, MultiValueRemoveProps, OptionProps, SelectInstance, SingleValueProps } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import UserTagsService from './user-tags-service';
import { AddUsersTagPostModel, CreateUserTagPostModel, RemoveUsersTagPostModel, UsersTagResponseModel, UserTagResponseModel } from '../../swagger-clients/s365-admin-panel-clients.service';
import { toast } from 'react-toastify';
import { _copyAndSort } from '../../utilities/array.helpers';

export interface TagOption {
    value: string;
    label: string;
    isSystemTag: boolean;
    color?: string;
}

interface UserTagPickerProps {
    userId: number;
    tags: UsersTagResponseModel[];
    onTagsChanged: (tags: TagOption[]) => void;
    onTagCreated: () => void;
}

export type UserTagPickerType = {
    getSuggestions: () => void;
}

const UserTagPicker = React.forwardRef<UserTagPickerType, UserTagPickerProps>((props, ref) => {
    const [keyCount, setKeyCount] = React.useState<number>(0);
    React.useImperativeHandle(ref, () => ({
        getSuggestions: () => { reloadOptions(); }
    }));

    const [currentValue, setCurrentValue] = React.useState<string>("");
    const [selectedValues, setSelectedValues] = React.useState<TagOption[]>();

    React.useEffect(() => {
        const tags = props.tags?.map(tag => ({ value: tag.userTagId.toString(), label: tag.name, isSystemTag: tag.isSystemTag, color: tag.color } as TagOption)) ?? [];
        setSelectedValues(tags);
    }, [props.tags]);

    const reloadOptions = () => {

        setKeyCount(keyCount + 1);
    }

    const getSuggestions = async (ignoreCache?: boolean) => {
        try {
              //  console.log(`getSuggestions called ${props.userId}`,ignoreCache);
            const userTagsResp = await UserTagsService.getUserTagsPromise(ignoreCache);
            const mappedOptions = userTagsResp.items?.map(x => ({ value: x.id.toString(), label: x.name, isSystemTag: x.isSystemTag, color: x.color } as TagOption)) ?? [];
            const sortedMappedOptions = _copyAndSort<TagOption>(mappedOptions, "label", false);
            return sortedMappedOptions;
        } catch (error) {
            console.error("Error fetching tags:", error);
            return [];
        }
    };
    const addSelectedSuggestion = async (tag: TagOption) => {
        try {
            const client = getUserTagsClient();

            const model = new AddUsersTagPostModel({
                tagId: +tag.value,
                userId: props.userId
            });

            await client.addUsersTag(model);
            props.onTagsChanged([...(selectedValues ?? []), tag]);

        } catch (error) {
            toast.error("An error occurred while adding tag.");
        }
    }

    const addTagByName = async (tagName: string) => {

        try {
            const client = getUserTagsClient();
            const createTagModel = new CreateUserTagPostModel({
                name: tagName
            });
            const createdTag = await client.createTag(createTagModel);
            const createTagOption = { value: createdTag.id.toString(), label: createdTag.name } as TagOption;
            await addSelectedSuggestion(createTagOption);
            props.onTagsChanged([...(selectedValues ?? []), createTagOption]);
            props.onTagCreated();

        } catch (error) {
            toast.error("An error occurred while adding tag.");
        }
    }

    const onDeleteTag = async (tagId: number) => {
        try {
            const client = getUserTagsClient();
            const model = new RemoveUsersTagPostModel({
                tagId: tagId,
                userId: props.userId
            });
            await client.removeUsersTag(model);
            const filteredValues = selectedValues?.filter(x => +x.value !== tagId) ?? [];
            props.onTagsChanged([...(filteredValues ?? [])]);

        } catch (error) {
            toast.error("An error occurred while deleting tag.");
        }
    }

    const onTagsChanged = async (selectedOptions: MultiValue<TagOption>, actionMeta: ActionMeta<TagOption>) => {
        console.log("onTagsChanged", selectedOptions, actionMeta);

        // on option selected
        if (actionMeta.action == "select-option") {
            const selectedOption = selectedOptions[selectedOptions.length - 1];
            await addSelectedSuggestion(selectedOption);
        }

        // on tag removed
        if (actionMeta.action == "remove-value") {
            if (actionMeta.removedValue.isSystemTag) { return; }
            onDeleteTag(+actionMeta.removedValue.value);
        }
    }

    const getMultiValueLabelStyles = (provided: CSSObjectWithLabel, props: MultiValueProps<TagOption, true, GroupBase<TagOption>>) => {
        let styles = {
            lineHeight: "normal",
            color: "white",
            backgroundColor: !!props.data.color ? props.data.color : "#b3b3b3"
        } as CSSObjectWithLabel;
        return { ...provided, ...styles };
    }

    const getControlStyles = (provided: CSSObjectWithLabel) => {
        let styles = {
            border: "none !important",
            boxShadow: "none",
            background: "inherit"
        } as CSSObjectWithLabel;
        return { ...provided, ...styles };
    }


    const Option: React.ComponentType<OptionProps<TagOption, true, GroupBase<TagOption>>> = (option): JSX.Element => {
        if (option.data.isSystemTag)
            return null;

        return (
            <components.Option {...(option as any)}>
                <div>
                    {option.data.label}
                </div>
            </components.Option>

        );
    }

    const MultiValueRemove = (props: MultiValueRemoveProps<TagOption>) => {
        if (props.data.isSystemTag) {
            return null;
        }
        return (

            <components.MultiValueRemove {...props} >

            </components.MultiValueRemove>

        );
    };

    const MultiValueLabel = (props: MultiValueGenericProps<TagOption>) => {
        return (
            <components.MultiValueLabel {...props} />
        );
    };

    return <AsyncCreatableSelect
        key={`${props.userId}-${keyCount}`}
        styles={{
            multiValueLabel: getMultiValueLabelStyles,
            multiValueRemove: getMultiValueLabelStyles,
            control: getControlStyles,
            indicatorSeparator: (provided) => ({ ...provided, display: "none" }),
            indicatorsContainer: (provided) => ({ ...provided, display: "none" }),
            menu: (base) => ({ ...base, width: "max-content", minWidth: "100%" }),
            //  multiValueRemove: (provided) => ({ ...provided, display: props.hasRemove ? "flex" : "none" }),         

        }}        
        value={selectedValues}
        isMulti
        isClearable={false}
        placeholder=""
        defaultOptions={true}
        loadOptions={() => getSuggestions(true)}        
        onInputChange={(newValue: string) => { setCurrentValue(newValue); }}
        onCreateOption={addTagByName}
        onChange={onTagsChanged}
        components={{ Option: Option, MultiValueRemove: MultiValueRemove }}

    />


});

export default UserTagPicker;